import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { selectCustomerData } from 'Core/redux/customer/selectors';
import { selectStatuses } from 'Core/redux/status/selectors';
import { useTransformOptions } from 'Core/hooks';
import { TextEditor } from 'Core/components/editor';
import { DocumentViewerModal, CarrierSelect } from 'Core/components';
import CustomPropTypes from 'CustomPropTypes';
import { ShipmentService } from '../../service/api/shipment-service';
import { saveAs } from 'Core/utils';

const getCustomerId = customer =>
  customer?.id ?? customer?.Id ?? customer?.customerId ?? customer?.CustomerId;

const getCustomerName = customer =>
  String(
    customer?.customerName ??
      customer?.CustomerName ??
      customer?.name ??
      customer?.Name ??
      '',
  ).trim();

const getCustomerShortName = customer =>
  String(customer?.customerShortName ?? customer?.CustomerShortName ?? '').trim();

/** Keep the menu above the shipment modal. Input height override lets the field stay typeable. */
const customerSelectStyles = {
  control: base => ({
    ...base,
    minHeight: 38,
    cursor: 'text',
  }),
  valueContainer: base => ({
    ...base,
    paddingLeft: 8,
    paddingRight: 8,
  }),
  input: base => ({
    ...base,
    height: 'auto',
    minHeight: 20,
    margin: 0,
    padding: 0,
    opacity: 1,
    color: '#212529',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1080,
  }),
  menu: base => ({
    ...base,
    zIndex: 1080,
  }),
};

const initData = {
  id: 0,
  customerId: '',
  shipDate: '',
  asp: '',
  packingSlipNumber: '',
  trailer: '',
  carrier: '',
  carrierId: '',
  expedited: 'No',
  status: '',
  poNumber: '',
  shipTo: '',
  address1: '',
  address2: '',
  note: '',
};

const getStatusName = statusItem =>
  String(statusItem?.status ?? statusItem?.Status ?? '').trim();

const getStatusSeq = statusItem => {
  if (!statusItem) return 0;
  const seq = statusItem.seq ?? statusItem.Seq;
  const parsed = Number(seq);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isIncludedInSearch = statusItem => {
  const value = statusItem?.includeInSearch ?? statusItem?.IncludeInSearch;
  return Number(value) === 1 || value === true || value === '1';
};

const normalizeStatusName = name =>
  String(name || '')
    .trim()
    .replace(/^"+|"+$/g, '')
    .toLowerCase();

const sortBySeqThenName = (a, b) =>
  getStatusSeq(a) - getStatusSeq(b) || getStatusName(a).localeCompare(getStatusName(b));

const base64ToPdfBlob = base64 => {
  const binary = atob(String(base64 || '').replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'application/pdf' });
};

const ShipmentAddEditForm = ({ data, onChanges, id, readOnlyAsp = false }) => {
  const customers = useSelector(selectCustomerData);
  const statuses = useSelector(selectStatuses);

  // Local to this form. Label is customerName from customer/getall, same as Planning workspace.
  // Stored value stays customer id so save and the rest of shipping are unchanged.
  const customerOptions = useMemo(() => {
    const list = Array.isArray(customers) ? customers : [];
    const byName = new Map();
    list.forEach(customer => {
      const id = getCustomerId(customer);
      const name = getCustomerName(customer);
      if (id === undefined || id === null || id === '' || !name || byName.has(name)) return;
      byName.set(name, { value: id, label: name, shortName: getCustomerShortName(customer) });
    });
    return [...byName.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const [formData, setFormData] = useState(data ? data : initData);
  // Keep the shipment's original status as the filter baseline (do not loosen after user picks forward)
  const [baselineStatus, setBaselineStatus] = useState(data?.status || '');
  const [shipDoc, setShipDoc] = useState(null);
  const [shipDocLoading, setShipDocLoading] = useState(false);
  const [shipDocError, setShipDocError] = useState('');
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState('');
  const [documentViewerName, setDocumentViewerName] = useState('');
  const documentViewerBlobUrlRef = useRef(null);

  const revokeDocumentViewerUrl = useCallback(() => {
    if (documentViewerBlobUrlRef.current) {
      URL.revokeObjectURL(documentViewerBlobUrlRef.current);
      documentViewerBlobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (data) {
      setFormData(data);
      setBaselineStatus(data.status || '');
    }
  }, [data?.id, data?.status]);

  useEffect(() => {
    return () => {
      revokeDocumentViewerUrl();
    };
  }, [revokeDocumentViewerUrl]);

  useEffect(() => {
    const asp = String(formData?.asp || data?.asp || '').trim();
    if (!asp) {
      setShipDoc(null);
      setShipDocError('');
      setShipDocLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadShipDoc = async () => {
      setShipDocLoading(true);
      setShipDocError('');
      setShipDoc(null);
      try {
        const response = await ShipmentService.getShipDocByAsp(asp);
        if (cancelled) return;
        if (response?.status === 200 && response?.response) {
          const payload = response.response;
          setShipDoc({
            exists: Boolean(payload.exists ?? payload.Exists),
            fileName: payload.fileName || payload.FileName || `${asp}.pdf`,
            fileBase64: payload.fileBase64 || payload.FileBase64 || null,
            webUrl: payload.webUrl || payload.WebUrl || null,
          });
        } else {
          setShipDoc({ exists: false, fileName: `${asp}.pdf`, fileBase64: null, webUrl: null });
        }
      } catch (err) {
        if (cancelled) return;
        setShipDoc(null);
        setShipDocError(err?.message || 'Failed to load ship document');
      } finally {
        if (!cancelled) setShipDocLoading(false);
      }
    };

    loadShipDoc();
    return () => {
      cancelled = true;
    };
  }, [formData?.asp, data?.asp]);

  const openShipDoc = useCallback(() => {
    if (shipDoc?.fileBase64) {
      const blob = base64ToPdfBlob(shipDoc.fileBase64);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    if (shipDoc?.webUrl) {
      window.open(shipDoc.webUrl, '_blank', 'noopener,noreferrer');
    }
  }, [shipDoc]);

  const openShipDocInViewer = useCallback(() => {
    if (!shipDoc?.exists) return;

    revokeDocumentViewerUrl();

    let url = '';
    if (shipDoc.fileBase64) {
      const blob = base64ToPdfBlob(shipDoc.fileBase64);
      url = URL.createObjectURL(blob);
      documentViewerBlobUrlRef.current = url;
    } else if (shipDoc.webUrl) {
      url = shipDoc.webUrl;
    }

    if (!url) return;

    setDocumentViewerUrl(url);
    setDocumentViewerName(shipDoc.fileName || 'Ship Doc');
    setShowDocumentViewer(true);
  }, [shipDoc, revokeDocumentViewerUrl]);

  const closeDocumentViewer = useCallback(() => {
    setShowDocumentViewer(false);
    setDocumentViewerUrl('');
    setDocumentViewerName('');
    revokeDocumentViewerUrl();
  }, [revokeDocumentViewerUrl]);

  const downloadShipDoc = useCallback(() => {
    if (shipDoc?.fileBase64) {
      const blob = base64ToPdfBlob(shipDoc.fileBase64);
      saveAs(blob, shipDoc.fileName || 'ship-doc.pdf');
      return;
    }
    if (shipDoc?.webUrl) {
      window.open(shipDoc.webUrl, '_blank', 'noopener,noreferrer');
    }
  }, [shipDoc]);

  const statusList = useMemo(() => {
    if (Array.isArray(statuses)) return statuses;
    if (Array.isArray(statuses?.response)) return statuses.response;
    return [];
  }, [statuses]);

  // 1) Only IncludeInSearch = 1
  // 2) Seq forward filter only when current status is Loading or higher Seq
  const filteredStatuses = useMemo(() => {
    const currentStatusName = baselineStatus || formData?.status;
    let list = statusList.filter(isIncludedInSearch);

    // Keep current status visible even if IncludeInSearch = 0
    if (currentStatusName) {
      const currentRow = statusList.find(
        s => normalizeStatusName(getStatusName(s)) === normalizeStatusName(currentStatusName),
      );
      if (
        currentRow &&
        !list.some(
          s => normalizeStatusName(getStatusName(s)) === normalizeStatusName(currentStatusName),
        )
      ) {
        list = [...list, currentRow];
      }
    }

    if (!currentStatusName || statusList.length === 0) {
      return list.slice().sort(sortBySeqThenName);
    }

    const current = statusList.find(
      s => normalizeStatusName(getStatusName(s)) === normalizeStatusName(currentStatusName),
    );
    if (!current) {
      return list.slice().sort(sortBySeqThenName);
    }

    const loading = statusList.find(
      s => normalizeStatusName(getStatusName(s)) === 'loading',
    );
    const loadingSeq = loading ? getStatusSeq(loading) : 5;
    const currentSeq = getStatusSeq(current);

    // Apply Seq restriction only for Loading and statuses with Seq >= Loading
    if (currentSeq >= loadingSeq) {
      list = list.filter(s => getStatusSeq(s) >= currentSeq);
    }

    return list.slice().sort(sortBySeqThenName);
  }, [statusList, baselineStatus, formData?.status]);

  const statusesOptions = useTransformOptions(
    filteredStatuses,
    'status',
    'status',
    '',
    'Select Status',
  );

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
    onChanges({ ...formData, [fieldName]: value });
  };

  const applyCarrierSelection = option => {
    const next = {
      ...formData,
      carrierId: option?.id || '',
      carrier: option?.name || '',
    };
    setFormData(next);
    onChanges(next);
  };

  const selectedCarrier = useMemo(() => {
    const id = formData.carrierId;
    const name = String(formData.carrier || '').trim();
    const hasId = id !== undefined && id !== null && String(id).trim() !== '' && String(id) !== '0';
    if (!hasId && !name) return null;
    return { id: hasId ? id : null, name };
  }, [formData.carrierId, formData.carrier]);

  const applyCustomerSelection = option => {
    const customerId =
      option?.value === undefined || option?.value === null ? '' : String(option.value);
    const next = {
      ...formData,
      customerId,
      // Tile shows short name only. Drop the previous full customerName so save
      // does not echo it back onto the list.
      customerName: null,
      customerShortName: option?.shortName || null,
    };
    setFormData(next);
    onChanges(next);
  };

  const selectedCustomerOption = useMemo(() => {
    const current = formData.customerId;
    if (current === '' || current === null || current === undefined || String(current) === '0') {
      return null;
    }
    return customerOptions.find(option => String(option.value) === String(current)) || null;
  }, [formData.customerId, customerOptions]);

  const expeditedList = [
    { value: 'No', label: 'No' },
    { value: 'Yes', label: 'Yes' },
  ];

  return (
    <> 
      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Customer <span className="text-danger">*</span>
            </label>
            <Select
              inputId="shipment-customer"
              className="shipment-filter-select"
              classNamePrefix="react-select"
              options={customerOptions}
              value={selectedCustomerOption}
              onChange={applyCustomerSelection}
              isSearchable
              isClearable={false}
              placeholder="Select Customer"
              styles={customerSelectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              aria-label="Customer"
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Date <span className="text-danger">*</span>
            </label>
            <div className="input-groupicon calender-input">
              {/* <Calendar className="info-img" /> */}
              <input
                type="date"
                // value={reformatDate(formData.shipDate)}
                value={formData.shipDate ? formData.shipDate.slice(0, 10) : ''}
                onChange={e => updateFormField('shipDate', e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              ASP/Shipment Number <span className="text-danger">*</span>
            </label>
            <input
              name="asp"
              type="text"
              className={readOnlyAsp ? 'form-control bg-light' : 'form-control'}
              value={formData.asp}
              readOnly={readOnlyAsp}
              title={readOnlyAsp ? 'ASP / Shipment number cannot be changed after the shipment is created' : undefined}
              onChange={e => updateFormField('asp', e.target.value)}
            />
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Packing Slip Number <span className="text-danger">*</span>
            </label>
            <input
              name="packingSlipNumber"
              type="text"
              className="form-control"
              value={formData.packingSlipNumber}
              onChange={e => updateFormField('packingSlipNumber', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Trailer</label>
            <input
              type="text"
              className="form-control"
              value={formData.trailer}
              name="trailer"
              onChange={e => updateFormField('trailer', e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Carrier</label>
            <CarrierSelect
              id="shipment-carrier"
              value={selectedCarrier}
              onChange={applyCarrierSelection}
              placeholder="Search for a carrier…"
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Expedited</label>

            <select
              value={formData.expedited}
              onChange={e => updateFormField('expedited', e.target.value)}
              className="form-control"
            >
              {expeditedList.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>
              Status <span className="text-danger">*</span>
            </label>
            <select
              value={formData.status}
              onChange={e => updateFormField('status', e.target.value)}
              className="form-control"
              required
            >
              {statusesOptions.map(option => (
                <option key={`${option.value}-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>PO Number</label>
            <input
              type="text"
              name="poNumber"
              className="form-control"
              value={formData.poNumber}
              onChange={e => updateFormField('poNumber', e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Shipping To</label>
            <input
              type="text"
              className="form-control"
              value={formData.shipTo}
              name="shipTo"
              onChange={e => updateFormField('shipTo', e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Address Line1</label>
            <input
              type="text"
              className="form-control"
              value={formData.address1}
              name="address1"
              onChange={e => updateFormField('address1', e.target.value)}
            />
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Address Line2</label>
            <input
              type="text"
              className="form-control"
              value={formData.address2}
              name="address2"
              onChange={e => updateFormField('address2', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Ship Doc</label>
            {!String(formData?.asp || '').trim() ? (
              <input
                type="text"
                className="form-control bg-light"
                value=""
                readOnly
                placeholder="Enter ASP to load document"
              />
            ) : shipDocLoading ? (
              <input
                type="text"
                className="form-control bg-light"
                value="Loading..."
                readOnly
              />
            ) : shipDocError ? (
              <input
                type="text"
                className="form-control bg-light text-danger"
                value={shipDocError}
                readOnly
                title={shipDocError}
              />
            ) : shipDoc?.exists ? (
              <div className="d-flex align-items-center gap-2">
                <input
                  type="text"
                  className="form-control bg-light"
                  value={shipDoc.fileName || ''}
                  readOnly
                  title={shipDoc.fileName || ''}
                />
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  title="Open in Document Viewer"
                  onClick={openShipDocInViewer}
                  disabled={!shipDoc.fileBase64 && !shipDoc.webUrl}
                >
                  <i className="feather-file-text" />
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  title="View in new tab"
                  onClick={openShipDoc}
                  disabled={!shipDoc.fileBase64 && !shipDoc.webUrl}
                >
                  <i className="feather-eye" />
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  title="Download"
                  onClick={downloadShipDoc}
                  disabled={!shipDoc.fileBase64 && !shipDoc.webUrl}
                >
                  <i className="feather-download" />
                </button>
              </div>
            ) : (
              <input
                type="text"
                className="form-control bg-light"
                value="No document found"
                readOnly
              />
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <div className="input-blocks summer-description-box">
            <label>Notes</label>
            <div id="summernote" />
            <TextEditor data={formData.note} onChange={val => updateFormField('note', val)} />
          </div>
        </div>
      </div>

      <DocumentViewerModal
        key={documentViewerUrl || 'ship-doc-viewer'}
        show={showDocumentViewer}
        onHide={closeDocumentViewer}
        documentUrl={documentViewerUrl}
        documentName={documentViewerName}
        onDownload={downloadShipDoc}
      />
    </>
  );
};

ShipmentAddEditForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape(CustomPropTypes.Shipment),
  onChanges: PropTypes.func,
  readOnlyAsp: PropTypes.bool,
};

export default ShipmentAddEditForm;
